import React from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { Home, FileText, Plus, Users, Settings, LogOut, Menu, CreditCard, Shield, Crown, UserCircle, Sun, Moon, LayoutGrid } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ThemeToggle, useTheme } from "@/components/ThemeToggle";
import NotificationsBell from "@/components/NotificationsBell";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DOC_TYPE_OPTIONS } from "@/lib/docTypeOptions";

const bottomItems = [
  { to: "/dashboard", icon: Home, label: "Início", testid: "nav-home" },
  { to: "/documents", icon: FileText, label: "Documentos", testid: "nav-documents" },
  { to: "/documents/new", icon: Plus, label: "Novo", testid: "nav-new", primary: true },
  { to: "/clients", icon: Users, label: "Clientes", testid: "nav-clients" },
];

export default function Layout({ company }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { dark, toggle: toggleTheme } = useTheme();
  const [openNew, setOpenNew] = React.useState(false);
  const [moreTypes, setMoreTypes] = React.useState(false);
  const isActive = (to) => location.pathname === to || (to !== "/dashboard" && location.pathname.startsWith(to));
  const topNav = React.useMemo(() => bottomItems.filter((i) => !i.primary), []);
  const [sub, setSub] = React.useState(null);
  React.useEffect(() => { api.get("/subscription").then(({ data }) => setSub(data)).catch(() => {}); }, [location.pathname === "/plans"]);
  const planBadge = sub && (sub.is_trial
    ? { text: `Teste • ${sub.trial_days_left}d`, cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30" }
    : { text: sub.info?.label || sub.plan, cls: "bg-primary/10 text-primary border-primary/30" });

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-0">
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-background/90 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 h-14 sm:h-16 flex items-center justify-between gap-2">
          <Link to="/dashboard" className="flex items-center gap-2 min-w-0" data-testid="brand-link">
            {company?.logo ? (
              <img src={company.logo} alt="logo" className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg object-cover border border-border shrink-0" />
            ) : (
              <img src="/np-logo.png" alt="NotaHoje" className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg object-contain bg-white border border-border p-0.5 shrink-0" />
            )}
            <div className="leading-tight min-w-0">
              <p className="font-display font-bold text-sm sm:text-base truncate">{company?.fantasy_name || company?.name || "NotaHoje"}</p>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {topNav.map((i) => (
              <Link key={i.to} to={i.to} data-testid={`top-${i.testid}`}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive(i.to) ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>
                {i.label}
              </Link>
            ))}
            <Link to="/settings" data-testid="top-nav-settings" className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive("/settings") ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>Configurações</Link>
            <button onClick={() => setOpenNew(true)} data-testid="top-nav-create" className="ml-2 h-9 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity flex items-center gap-1"><Plus className="h-4 w-4" /> Criar documento</button>
          </nav>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {planBadge && (
              <Link to="/plans" data-testid="plan-badge" title="Minha assinatura"
                className={`inline-flex items-center gap-1 rounded-full border px-2 sm:px-2.5 h-8 text-[11px] font-semibold whitespace-nowrap transition-colors hover:opacity-90 ${planBadge.cls}`}>
                <Crown className="h-3.5 w-3.5" /><span className="hidden min-[400px]:inline">{planBadge.text}</span>
              </Link>
            )}
            <NotificationsBell />
            <div className="hidden md:block"><ThemeToggle /></div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="profile-menu-btn" className="flex items-center gap-1.5 rounded-xl border border-border bg-card px-1.5 sm:px-2 h-9 hover:bg-accent transition-colors">
                  <Avatar className="h-7 w-7"><AvatarFallback className="text-xs bg-primary text-primary-foreground">{(user?.name || "U").slice(0, 1).toUpperCase()}</AvatarFallback></Avatar>
                  <Menu className="h-4 w-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-semibold truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings?tab=conta")} data-testid="menu-account"><UserCircle className="h-4 w-4 mr-2" />Meu usuário</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings"><Settings className="h-4 w-4 mr-2" />Configurações</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings?tab=mais")} data-testid="menu-more"><LayoutGrid className="h-4 w-4 mr-2" />Mais recursos</DropdownMenuItem>
                {user?.role !== "tecnico" && <DropdownMenuItem onClick={() => navigate("/plans")} data-testid="menu-plans"><CreditCard className="h-4 w-4 mr-2" />Minha Assinatura</DropdownMenuItem>}
                {user?.is_admin && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><Shield className="h-4 w-4 mr-2" />Painel Admin</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={(e) => { e.preventDefault(); toggleTheme(); }} data-testid="menu-theme" className="md:hidden">{dark ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}{dark ? "Tema claro" : "Tema escuro"}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-destructive"><LogOut className="h-4 w-4 mr-2" />Sair</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <Outlet />
      </main>

      <footer className="max-w-6xl mx-auto px-4 pb-8 pt-4 flex flex-col items-center gap-1 text-center" data-testid="app-footer">
        <div className="flex items-center gap-2 rounded-xl bg-white px-3 py-1.5" data-testid="app-footer-logo">
          <img src="/nh-wordmark.png" alt="NotaHoje" className="h-8 object-contain" />
        </div>
        <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} NotaHoje • Documentos profissionais em segundos</p>
      </footer>

      {/* Bottom nav (mobile) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur border-t border-border shadow-[0_-6px_20px_rgba(0,0,0,0.08)] dark:shadow-[0_-6px_20px_rgba(0,0,0,0.5)]">
        <div className="grid grid-cols-5 h-20">
          {[bottomItems[0], bottomItems[1], bottomItems[2], bottomItems[3], { to: "/settings", icon: Settings, label: "Ajustes", testid: "nav-settings" }].map((i) => {
            const Icon = i.icon;
            const active = isActive(i.to);
            if (i.primary) {
              return (
                <button key={i.to} onClick={() => setOpenNew(true)} data-testid="bottom-nav-new" className="flex flex-col items-center justify-center">
                  <div className="h-14 w-14 -mt-6 rounded-2xl bg-primary shadow-lg shadow-primary/30 flex items-center justify-center text-primary-foreground active:scale-95 transition-transform">
                    <Icon className="h-7 w-7" />
                  </div>
                  <span className="text-[10px] mt-0.5 font-medium">{i.label}</span>
                </button>
              );
            }
            return (
              <Link key={i.to} to={i.to} data-testid={`bottom-${i.testid}`} className={`flex flex-col items-center justify-center gap-1 transition-colors ${active ? "text-primary" : "text-muted-foreground dark:text-foreground/70"}`}>
                <Icon className="h-5 w-5" />
                <span className="text-[10px] font-medium">{i.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>O que você quer criar?</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2.5">
            {[
              ...DOC_TYPE_OPTIONS.filter((o) => ["nota_servico", "budget", "report"].includes(o.type)).map((o) => ({ key: o.type, to: `/documents/new/${o.type}`, label: o.label, Icon: o.Icon, color: o.color, testid: `quick-new-${o.type}` })),
              { key: "client", to: "/clients?new=1", label: "Cliente", Icon: Users, color: "bg-primary/10 text-primary", testid: "quick-new-client" },
            ].map((o) => (
              <button key={o.key} onClick={() => { setOpenNew(false); navigate(o.to); }} data-testid={o.testid}
                className="p-4 rounded-xl border border-border hover:border-primary hover:bg-accent transition-all active:scale-95 flex flex-col items-center gap-2 text-center">
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${o.color}`}><o.Icon className="h-6 w-6" /></div>
                <span className="text-sm font-semibold leading-tight">{o.label}</span>
              </button>
            ))}
          </div>
          <button onClick={() => setMoreTypes((v) => !v)} className="text-sm text-primary font-semibold mt-1" data-testid="quick-new-more">{moreTypes ? "Menos tipos" : "Mais tipos de documento"}</button>
          {moreTypes && (
            <div className="grid grid-cols-2 gap-2">
              {DOC_TYPE_OPTIONS.filter((o) => !["nota_servico", "budget", "report"].includes(o.type)).map((o) => (
                <button key={o.type} onClick={() => { setOpenNew(false); navigate(`/documents/new/${o.type}`); }} data-testid={`quick-new-${o.type}`}
                  className="p-2.5 rounded-xl border border-border hover:border-primary hover:bg-accent transition-all active:scale-95 flex items-center gap-2 text-left">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${o.color}`}><o.Icon className="h-4 w-4" /></div>
                  <span className="text-xs font-semibold leading-tight">{o.label}</span>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
